import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderKanbanIcon, FlaskConicalIcon, PenToolIcon, MusicIcon, ImageIcon, UserIcon, MailIcon, PlusIcon, ActivityIcon } from 'lucide-react';
import { useProjects } from '../../lib/store';
import { useExperiments, useWriting, useMusic, useMediaLibrary } from '../../lib/cmsStore';
import { Button } from '../../components/ui/Button';
export function AdminDashboard() {
  const navigate = useNavigate();
  const {
    projects
  } = useProjects();
  const {
    items: experiments
  } = useExperiments();
  const {
    items: writing
  } = useWriting();
  const {
    items: music
  } = useMusic();
  const {
    items: media
  } = useMediaLibrary();
  const allItems = [...projects.map(p => ({
    ...p,
    type: 'Project',
    path: `/admin/work/${p.id}`
  })), ...experiments.map(e => ({
    ...e,
    type: 'Experiment',
    path: `/admin/experiments`
  })), ...writing.map(w => ({
    ...w,
    type: 'Article',
    path: `/admin/writing`
  })), ...music.map(m => ({
    ...m,
    type: 'Music',
    path: `/admin/music`
  }))].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const recentActivity = allItems.slice(0, 5);
  const totalPublished = allItems.filter(i => i.status === 'published').length;
  const stats = [{
    label: 'Projects',
    value: projects.length
  }, {
    label: 'Experiments',
    value: experiments.length
  }, {
    label: 'Articles',
    value: writing.length
  }, {
    label: 'Music',
    value: music.length
  }, {
    label: 'Media Files',
    value: media.length
  }, {
    label: 'Published',
    value: totalPublished
  }];
  const sections = [{
    title: 'About',
    icon: UserIcon,
    path: '/admin/about',
    count: '-'
  }, {
    title: 'Work',
    icon: FolderKanbanIcon,
    path: '/admin/work',
    count: projects.length
  }, {
    title: 'Experiments',
    icon: FlaskConicalIcon,
    path: '/admin/experiments',
    count: experiments.length
  }, {
    title: 'Writing',
    icon: PenToolIcon,
    path: '/admin/writing',
    count: writing.length
  }, {
    title: 'Music',
    icon: MusicIcon,
    path: '/admin/music',
    count: music.length
  }, {
    title: 'Contact',
    icon: MailIcon,
    path: '/admin/contact',
    count: '-'
  }];
  return <div className="p-8 max-w-7xl mx-auto space-y-12 pb-24" data-id="element-595">
      <header className="flex items-center justify-between" data-id="element-596">
        <div data-id="element-597">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-598">
            Dashboard
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-599">
            Overview of your portfolio content.
          </p>
        </div>
      </header>

      {/* Stats Grid */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-600">
        {stats.map((stat, i) => <div key={i} className="border border-border bg-card p-6 flex flex-col items-center justify-center text-center" data-id="element-601">
            <span className="font-display text-4xl font-bold text-foreground mb-2" data-id="element-602">
              {stat.value}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-603">
              {stat.label}
            </span>
          </div>)}
      </section>

      {/* Quick Actions */}
      <section data-id="element-604">
        <div className="border-b border-border pb-2 mb-6" data-id="element-605">
          <h2 className="font-display text-2xl font-bold" data-id="element-606">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-607">
          <Button onClick={() => navigate('/admin/work/new')} variant="outline" className="h-16 gap-3 justify-start px-6" data-id="element-608">
            <PlusIcon className="w-4 h-4 text-primary" data-id="element-609" /> New Project
          </Button>
          <Button onClick={() => navigate('/admin/experiments?new=true')} variant="outline" className="h-16 gap-3 justify-start px-6" data-id="element-610">
            <PlusIcon className="w-4 h-4 text-primary" data-id="element-611" /> New Experiment
          </Button>
          <Button onClick={() => navigate('/admin/writing?new=true')} variant="outline" className="h-16 gap-3 justify-start px-6" data-id="element-612">
            <PlusIcon className="w-4 h-4 text-primary" data-id="element-613" /> New Article
          </Button>
          <Button onClick={() => navigate('/admin/music?new=true')} variant="outline" className="h-16 gap-3 justify-start px-6" data-id="element-614">
            <PlusIcon className="w-4 h-4 text-primary" data-id="element-615" /> New Music Entry
          </Button>
        </div>
      </section>

      {/* Content Sections */}
      <section data-id="element-616">
        <div className="border-b border-border pb-2 mb-6" data-id="element-617">
          <h2 className="font-display text-2xl font-bold" data-id="element-618">Manage Content</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-619">
          {sections.map(section => <Link key={section.title} to={section.path} className="group border border-border bg-card p-6 hover:border-primary transition-colors flex items-center justify-between" data-id="element-620">
              <div className="flex items-center gap-4" data-id="element-621">
                <div className="w-10 h-10 bg-secondary flex items-center justify-center group-hover:bg-primary/10 group-hover:text-primary transition-colors" data-id="element-622">
                  <section.icon className="w-5 h-5" data-id="element-623" />
                </div>
                <div data-id="element-624">
                  <h3 className="font-display text-lg font-bold group-hover:text-primary transition-colors" data-id="element-625">
                    {section.title}
                  </h3>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1" data-id="element-626">
                    {section.count !== '-' ? `${section.count} Items` : 'Page Content'}
                  </p>
                </div>
              </div>
            </Link>)}
        </div>
      </section>

      {/* Recent Activity */}
      <section data-id="element-627">
        <div className="border-b border-border pb-2 mb-6 flex items-center gap-2" data-id="element-628">
          <ActivityIcon className="w-5 h-5 text-muted-foreground" data-id="element-629" />
          <h2 className="font-display text-2xl font-bold" data-id="element-630">Recent Activity</h2>
        </div>
        <div className="border border-border bg-card divide-y divide-border" data-id="element-631">
          {recentActivity.length === 0 ? <div className="p-8 text-center text-muted-foreground font-mono text-sm" data-id="element-632">
              No recent activity found.
            </div> : recentActivity.map((item, i) => <div key={i} className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors" data-id="element-633">
                <div className="flex items-center gap-4" data-id="element-634">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-secondary text-muted-foreground w-24 text-center" data-id="element-635">
                    {item.type}
                  </span>
                  <div data-id="element-636">
                    <h4 className="font-display font-bold" data-id="element-637">{item.title}</h4>
                    <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-638">
                      Updated {new Date(item.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-639">
                  <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${item.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-640">
                    {item.status}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => navigate(item.path)} data-id="element-641">
                    Edit
                  </Button>
                </div>
              </div>)}
        </div>
      </section>
    </div>;
}